import { Controller } from "@hotwired/stimulus"

// Keeps the customer where they were standing when a button reloads the page.
//
// The +/- steppers on search reload the whole page: BookingButtonsHelper#toggle_data
// sets `turbo: false`, so each press is a full browser navigation and nothing Turbo
// offers — morphing, turbo-refresh-scroll, replace visits — applies. The page came back
// at the top and the room the customer was adjusting was gone off screen.
//
// The awkward part is that the results are NOT in the first paint: the search form
// auto-submits them in afterwards. For a second or two the document is a few hundred
// pixels tall, so restoring straight away scrolls to a position that does not exist yet
// and is then lost when the content arrives. So this waits for the page to grow tall
// enough to hold the offset before restoring it, and gives up rather than guessing if
// it never does — a room removed or a filter narrowed can legitimately leave a shorter
// page, and jumping somewhere arbitrary is worse than staying put.
export default class extends Controller {
  static KEY = "scroll-memory:offset"
  static TIMEOUT_MS = 10000

  connect() {
    this.save = this.save.bind(this)
    // On CLICK, not submit, and in the capture phase — these same buttons also carry
    // `click->toggle#toggleTarget`, which swaps the results out for a shorter skeleton.
    // That shrinks the document, the browser clamps the scroll down to fit, and by the
    // time `submit` fires the position worth keeping is already gone.
    document.addEventListener("click", this.save, true)
    this.restore()
  }

  disconnect() {
    document.removeEventListener("click", this.save, true)
    if (this.observer) this.observer.disconnect()
  }

  // Only the buttons that change the order and reload the page. Recording every click
  // would leave an offset behind for whatever page the customer opened next.
  save(event) {
    const form = event.target?.closest?.("form")
    if (!form || !(form.getAttribute("action") || "").includes("/order_item")) return

    try {
      sessionStorage.setItem(this.constructor.KEY, String(window.scrollY))
    } catch {
      // Private browsing and blocked site data both throw. Losing the scroll position
      // is a worse page, not a broken one.
    }
  }

  restore() {
    let stored = null
    try {
      stored = sessionStorage.getItem(this.constructor.KEY)
      // Read once: an ordinary navigation later must never inherit a stale offset.
      sessionStorage.removeItem(this.constructor.KEY)
    } catch {
      return
    }

    const target = parseInt(stored, 10)
    if (!Number.isFinite(target) || target <= 0) return

    this.awaitHeight(target)
  }

  // Watches the document until it can actually hold the offset. A MutationObserver
  // rather than a timer because the results arrive as one DOM change, not gradually.
  awaitHeight(target) {
    const tallEnough = () =>
      document.documentElement.scrollHeight >= target + window.innerHeight

    const done = () => {
      this.observer?.disconnect()
      clearTimeout(this.timer)
      // `instant`, NOT `auto`: per spec `auto` means "use the computed scroll-behavior",
      // and this page sets scroll-behavior: smooth — so `auto` began an animation that
      // never finished and left the page at the top. Restoring a position is a jump,
      // not a journey.
      window.scrollTo({ top: target, behavior: "instant" })
    }

    if (tallEnough()) return done()

    this.observer = new MutationObserver(() => { if (tallEnough()) done() })
    this.observer.observe(document.body, { childList: true, subtree: true })
    this.timer = setTimeout(() => this.observer?.disconnect(), this.constructor.TIMEOUT_MS)
  }
}
